import {
  ExchangeCandidateFilterEnum,
  LogonTypeEnum,
  SupporterLogonIdFieldEnum,
  SwapIsUsedEnum,
  UseDateTimeTypeEnum,
} from '@pdcplan/shared/enums';
import {
  TeamPlanSettings,
  TeamShiftDef,
  UserSettings,
} from '@pdcplan/shared/api/TeamPlan';

import { ActionType } from './ActionType';
import { AppAction } from './appActions';
import { ConfirmDialogProps } from './components/ConfirmDialog/ConfirmDialog';
import { getChatKey, type DayMessageMap } from './util/getChatKey';
import { GetVersionModel, toIdMap } from './api/model';
import { Holiday } from '@pdcplan/shared/api/Calendar';
import { InfoDialogProps } from './components/InfoDialog/InfoDialog';
import { LocalStorageKey } from './util/LocalStorageKey';
import { ModalWrapperProps } from './Modal/ModalWrapper';
import { PushNotificationAction } from './api/PushNotificationActions';
import { Reducer } from 'redux';
import { ToastType } from '@pdcfrontendui/components/Toast/Toast';
import { dateMath } from '@pdcfrontendui/utils';
import { isOffline } from './util/networkHandling';
import { localStorageGet } from './util/localStorage';
import { GlobalSettings } from '@pdcplan/shared/api/Common';
import { LoginAction, LoginEvents } from './Login/LoginAction';
import type { ListViewAction } from './ListView/ListViewActions';

export type AppReducer = {
  screensizeBig: boolean;
  shouldShowCalendar: boolean;
  shouldShowSearchBar: boolean;
  shouldShowMenu: boolean;
  modalLoading: boolean;
  /**
   * activeDate is always 00:00:00 for a given day
   */
  activeDate: Date;
  holidays: Holiday[];
  version: GetVersionModel | null;
  globalSettings: GlobalSettings;
  settings: TeamPlanSettings;
  userSettings: UserSettings;
  openModals: ModalWrapperProps[];
  errorMessage?: string;
  /**
   * Note: Contains only messages for the current team.
   */
  dayMessageMap: DayMessageMap;
  unreadCountMap: Record<string, number>;
  loadingGetVersion: boolean;
  loadingGetSettings: boolean;
  toast: ToastType | null;
  confirmDialog: ConfirmDialogProps;
  infoDialog: InfoDialogProps;
  errorModal: {
    error: any;
    shown: boolean;
    isConnectionError: boolean;
    title?: string;
  };
  showingRuleViolationInfo: boolean;
  failedPollingEndpoints: string[];
  /**
   * Key is TeamShiftDef.id
   * We don't need to scope this to a team, since all team shift def ids are unique
   */
  shiftDefMap: Record<string, TeamShiftDef>;
  hasInternet: boolean;
  connectionError: Error | null;
};

const initialDate = dateMath.floorDay(new Date());

const initialState: AppReducer = {
  screensizeBig: false,
  shouldShowCalendar: false,
  shouldShowSearchBar: false,
  shouldShowMenu: !localStorageGet(LocalStorageKey.currentTeam),
  modalLoading: false,
  activeDate: initialDate,
  holidays: [],
  version: null,
  globalSettings: {
    useDateTime: initialDate,
    useDateTimeType: UseDateTimeTypeEnum.unfrozen,
    enableBiometricAuthentication: false,
    idleLogoutSeconds: 300,
    logonMethod: LogonTypeEnum.staffWebUser,
    supporterLogonMethod: LogonTypeEnum.staffWebUser,
    supporterLogonIdFields: [SupporterLogonIdFieldEnum.salaryId],
    openIdAuthorizationEndpoint: null,
    openIdClientId: null,
    openIdRedirectUriEmployee: null,
    openIdRedirectUriPlanner: null,
    openIdScopeEmployee: null,
    openIdScopePlanner: null,
    openIdAcrValuesEmployee: null,
    openIdAcrValuesPlanner: null,
    openIdClientIdSupporter: null,
    openIdRedirectUriSupporter: null,
    openIdScopeSupporter: null,
    openIdAcrValuesSupporter: null,
    openIdResponseType: null,
    openIdResource: null,
    openIdLogoutEndpoint: null,
  },
  settings: {
    swapIsUsed: SwapIsUsedEnum.no,
    defaultWorkSalarysort: 'ERROR',
    possibleCandidateFilters: [],
    unreadMessageCountPollingSeconds: 30,
    messagesPollingSeconds: 30,
    teamRosterPollingSeconds: 30,
    activeScope: 7,
  },
  userSettings: {
    defaultExchangeCandidateFilter: ExchangeCandidateFilterEnum.classic,
    canSeeConfidentialInfo: false,
  },
  openModals: [],
  dayMessageMap: {},
  unreadCountMap: {},
  loadingGetSettings: false,
  loadingGetVersion: false,
  toast: null,
  confirmDialog: {
    buttonCancelText: '',
    buttonOkText: '',
    message: '',
    onCancel: undefined,
    onOk: () => undefined,
    shown: false,
    iconKey: undefined,
    title: '',
    variant: undefined,
  },
  infoDialog: {
    buttonText: '',
    message: '',
    onOk: () => undefined,
    shown: false,
    buttonId: undefined,
    title: undefined,
    variant: undefined,
    buttonStyle: undefined,
  },
  errorModal: {
    shown: false,
    error: null,
    isConnectionError: false,
    title: '',
  },
  showingRuleViolationInfo: false,
  shiftDefMap: {},
  failedPollingEndpoints: [],
  hasInternet: !isOffline(),
  connectionError: null,
};

const appReducer: Reducer<AppReducer> = (
  state: AppReducer = initialState,
  action: AppAction | PushNotificationAction | LoginAction | ListViewAction
) => {
  switch (action.type) {
    case LoginEvents.LOGOUT:
      return {
        ...initialState,
        globalSettings: state.globalSettings,
        version: state.version,
        shouldShowMenu: !localStorageGet(LocalStorageKey.currentTeam),
        screensizeBig: state.screensizeBig,
      };
    case ActionType.TOGGLE_SCREEN_SMALL: {
      return {
        ...state,
        screensizeBig: false,
      };
    }
    case ActionType.TOGGLE_SCREEN_BIG:
      return {
        ...state,
        screensizeBig: true,
      };

    case ActionType.CHANGE_DATE: {
      return {
        ...state,
        activeDate: action.date,
      };
    }
    case ActionType.TOGGLE_CALENDAR:
      return {
        ...state,
        shouldShowCalendar: !state.shouldShowCalendar,
      };
    case ActionType.TOGGLE_SEARCHBAR:
      return {
        ...state,
        shouldShowSearchBar: !state.shouldShowSearchBar,
      };
    case ActionType.TOGGLE_MENU:
      return {
        ...state,
        shouldShowMenu: !state.shouldShowMenu,
      };
    case ActionType.SET_MODALLOADING:
      return {
        ...state,
        modalLoading: action.modalLoading,
      };
    case ActionType.OPEN_MODAL: {
      const openModals = [...state.openModals];
      openModals.push(action.modal);
      return {
        ...state,
        openModals,
      };
    }
    case ActionType.CLOSE_MODAL: {
      const openModals = [...state.openModals];
      openModals.pop();
      return {
        ...state,
        openModals,
      };
    }
    case ActionType.GETVERSION_REQUEST:
      return { ...state, loadingGetVersion: true };
    case ActionType.GETVERSION_SUCCESS:
      return {
        ...state,
        version: action.getVersionResponse,
        loadingGetVersion: false,
      };
    case ActionType.GETVERSION_FAILURE:
      return {
        ...state,
        errorMessage: action.errorMessage,
        loadingGetVersion: false,
      };
    case ActionType.SET_VERSION:
      return {
        ...state,
        version: action.version,
        loadingGetVersion: false,
      };
    case ActionType.GETGLOBALSETTINGS_REQUEST:
      return { ...state, loadingGetSettings: true };
    case ActionType.GETGLOBALSETTINGS_SUCCESS: {
      const useDateTime =
        action.globalSettings.useDateTime ?? dateMath.floorDay(new Date());
      return {
        ...state,
        globalSettings: {
          ...action.globalSettings,
          useDateTime,
          useDateTimeType:
            action.globalSettings.useDateTimeType ??
            UseDateTimeTypeEnum.frozenTime,
        },
        activeDate: dateMath.floorDay(useDateTime), // set activeDate to the useDateTime whenever get settings is called
        loadingGetSettings: false,
      };
    }
    case ActionType.GETGLOBALSETTINGS_FAILURE:
      return {
        ...state,
        errorMessage: action.errorMessage,
        loadingGetSettings: false,
      };
    case ActionType.GETSETTINGS_FAILURE:
      return {
        ...state,
        errorMessage: action.errorMessage,
      };
    case ActionType.GETSETTINGS_SUCCESS:
      return {
        ...state,
        settings: {
          ...action.settings,
        },
      };
    case ActionType.GETUSERSETTINGS_FAILURE:
      return {
        ...state,
        errorMessage: action.errorMessage,
      };
    case ActionType.GETUSERSETTINGS_SUCCESS:
      return {
        ...state,
        userSettings: {
          ...action.userSettings,
        },
      };
    // Push notifications
    case ActionType.PUSHNOTIFICATION_REGISTER_ERROR:
    case ActionType.PUSHNOTIFICATION_RECEIVED:
    case ActionType.PUSHNOTIFICATION_ERROR:
      return {
        ...state,
      };
    case 'CHANGE_TEAM': {
      // Reset messages when changing team
      return { ...state, dayMessageMap: {}, activeDate: action.useDateTime };
    }
    case ActionType.GETMESSAGES_SUCCESS:
      return {
        ...state,
        dayMessageMap: { ...state.dayMessageMap, ...action.dayMessageMap },
      };
    case ActionType.MARK_THREAD_AS_READ:
      return state.dayMessageMap[action.payload]
        ? {
            ...state,
            dayMessageMap: {
              ...state.dayMessageMap,
              [action.payload]: state.dayMessageMap[action.payload]!.map(
                (message) =>
                  message.ReadWhen
                    ? message
                    : {
                        ...message,
                        ReadWhen: new Date(), // Value doesn't matter. But it's truthiness does.
                      }
              ),
            },
          }
        : state;
    case ActionType.GETUNREADMESSAGES_SUCCESS:
      return {
        ...state,
        unreadCountMap: { ...state.unreadCountMap, ...action.unreadCountMap },
      };
    case ActionType.POSTMESSAGE_REQUEST: {
      const chatKey = getChatKey(
        action.tempMessage.PersonId,
        action.tempMessage.Day,
        action.tempMessage.Visibility
      );
      return {
        ...state,
        dayMessageMap: {
          ...state.dayMessageMap,
          [chatKey]: [
            ...(state.dayMessageMap[chatKey] ?? []),
            action.tempMessage,
          ],
        },
      };
    }
    case ActionType.POSTMESSAGE_SUCCESS: {
      const chatKey = getChatKey(
        action.message.PersonId,
        action.message.Day,
        action.message.Visibility
      );
      return {
        ...state,
        dayMessageMap: {
          ...state.dayMessageMap,
          [chatKey]: [
            ...(state.dayMessageMap[chatKey]?.filter(
              (message) => message.MessageId !== action.tempMessage.MessageId
            ) ?? []),
            action.message,
          ],
        },
      };
    }
    case ActionType.POSTMESSAGE_FAILURE: {
      const chatKey = getChatKey(
        action.tempMessage.PersonId,
        action.tempMessage.Day,
        action.tempMessage.Visibility
      );
      if (!state.dayMessageMap[chatKey]?.length) {
        return state;
      }
      return {
        ...state,
        dayMessageMap: {
          ...state.dayMessageMap,
          [chatKey]: state.dayMessageMap[chatKey].filter(
            (message) => message.MessageId !== action.tempMessage.MessageId
          ),
        },
      };
    }
    case ActionType.GOT_HOLIDAYS: {
      return { ...state, holidays: action.holidays };
    }
    case ActionType.QUEUE_TOAST: {
      return { ...state, toast: action.toast };
    }
    case ActionType.SHOW_CONFIRM_DIALOG:
      return {
        ...state,
        confirmDialog: { ...action.payload, shown: true },
        infoDialog: { ...state.infoDialog, shown: false },
      };
    case ActionType.HIDE_CONFIRM_DIALOG:
      return {
        ...state,
        confirmDialog: { ...state.confirmDialog, shown: false },
      };
    case ActionType.SHOW_INFO_DIALOG:
      return {
        ...state,
        infoDialog: { ...action.payload, shown: true },
        confirmDialog: { ...state.confirmDialog, shown: false },
      };
    case ActionType.HIDE_INFO_DIALOG:
      return {
        ...state,
        infoDialog: { ...state.infoDialog, shown: false },
      };
    case ActionType.SHOW_ERROR_MODAL:
      return {
        ...state,
        errorModal: {
          error: action.payload.error,
          shown: true,
          title: action.payload.title,
          isConnectionError: action.payload.isConnectionError ?? false,
        },
      };
    case ActionType.HIDE_ERROR_MODAL:
      return {
        ...state,
        errorModal: {
          ...state.errorModal,
          shown: false,
          isConnectionError: false,
        },
      };
    case ActionType.SET_SHIFTDEFMAP:
      return {
        ...state,
        shiftDefMap: toIdMap(action.data),
      };
    case ActionType.SET_SHOWING_RULE_VIOLATION_INFO:
      return {
        ...state,
        showingRuleViolationInfo: action.payload,
      };
    case ActionType.ADD_FAILED_POLLING_ENDPOINT:
      return {
        ...state,
        failedPollingEndpoints: state.failedPollingEndpoints.includes(
          action.payload
        )
          ? state.failedPollingEndpoints
          : [...state.failedPollingEndpoints, action.payload],
      };
    case ActionType.REMOVE_FAILED_POLLING_ENDPOINT:
      return {
        ...state,
        failedPollingEndpoints: state.failedPollingEndpoints.filter(
          (endpoint) => endpoint !== action.payload
        ),
      };
    case ActionType.SET_HAS_INTERNET:
      return {
        ...state,
        hasInternet: action.payload,
      };
    case ActionType.SET_CONNECTION_ERROR:
      return {
        ...state,
        connectionError: action.payload,
      };
  }
  return state;
};

export default appReducer;

